"use client";
import { useState, FormEvent, useEffect, Suspense } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Mail, Lock, Eye, EyeOff, LogIn, Globe, CheckCircle, ArrowLeft } from "lucide-react";
import { signIn } from "next-auth/react";

function getDashboardByRole(role?: string): string {
  switch (role) {
    case "super_admin":
    case "admin":
    case "manager":
      return "/dashboard";
    case "viewer":
    case "support":
    case "coordinator":
    default:
      return "/dashboard/kpis";
  }
}

function LoginPage() {
  const searchParams = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  useEffect(() => {
    if (searchParams.get("reset") === "success") {
      setSuccessMessage("Your password has been reset successfully. You can now sign in with your new password.");
    }
  }, [searchParams]);

  // After Google OAuth redirect, sync NextAuth session → kpi_token cookie then go to dashboard
  useEffect(() => {
    if (searchParams.get("google") === "1") {
      setGoogleLoading(true);
      fetch("/api/auth/sync-google")
        .then((res) => res.json())
        .then((data) => {
          if (data.ok) {
            window.location.href = getDashboardByRole(data.role);
          } else {
            setError("Google sign-in failed. Please try again.");
            setGoogleLoading(false);
          }
        })
        .catch(() => {
          setError("Google sign-in failed. Please try again.");
          setGoogleLoading(false);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function validate() {
    const errors: { email?: string; password?: string } = {};
    if (!email) errors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Enter a valid email address.";
    if (!password) errors.password = "Password is required.";
    else if (password.length < 6) errors.password = "Password must be at least 6 characters.";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, rememberMe }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Invalid email or password. Please try again.");
        return;
      }
      // If the user was redirected here from a protected route, send them back there.
      // Use a full page navigation (window.location.href) instead of router.push() to
      // ensure the browser sends the newly-set kpi_token cookie on the next request.
      // router.push() can race against the cookie being committed, causing the middleware
      // to see no cookie and redirect back to /login.
      const from = searchParams.get("from");
      const destination = from && from.startsWith("/dashboard") ? from : getDashboardByRole(data.user?.role);
      window.location.href = destination;
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    setGoogleLoading(true);
    setError("");
    try {
      // After OAuth completes, NextAuth redirects to callbackUrl.
      // We use /login?google=1 so our useEffect can call sync-google and set kpi_token.
      await signIn("google", { callbackUrl: "/login?google=1" });
    } catch {
      setError("Google sign-in failed. Please try again.");
      setGoogleLoading(false);
    }
  }

  return (
    <div
      className="flex-1 flex flex-col items-center justify-center px-4 py-12 relative"
      style={{ background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 40%, #bfdbfe 100%)" }}
    >
      {/* Back to Home */}
      <Link
        href="/"
        className="absolute top-6 left-6 flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
      >
        <ArrowLeft size={15} />
        Back to Home
      </Link>
      {/* Card */}
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8">
          <Link href="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center font-bold text-sm text-white shadow-lg group-hover:bg-blue-700 transition-colors">
              M
            </div>
            <span className="text-xl font-bold tracking-tight" style={{ color: "var(--color-text-primary)" }}>
              MeritVision KPI Platform
            </span>
          </Link>
        </div>

        <div
          className="rounded-2xl p-8 shadow-lg"
          style={{ backgroundColor: "var(--color-bg-card)", border: "1px solid var(--color-border)" }}
        >
          <div className="mb-7">
            <h1 className="text-2xl font-bold mb-1.5" style={{ color: "var(--color-text-primary)" }}>Welcome back</h1>
            <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>Sign in to your account to continue</p>
          </div>

          {/* Google Sign-In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading || googleLoading}
            className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded-xl text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-5 border hover:bg-blue-50"
            style={{ backgroundColor: "var(--color-bg-input)", borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}
          >
            {googleLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" style={{ color: "var(--color-text-muted)" }}>
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                Connecting…
              </>
            ) : (
              <>
                <Globe size={17} style={{ color: "var(--color-text-muted)" }} />
                Continue with Google
              </>
            )}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px" style={{ backgroundColor: "var(--color-border)" }} />
            <span className="text-xs font-medium" style={{ color: "var(--color-text-muted)" }}>or sign in with email</span>
            <div className="flex-1 h-px" style={{ backgroundColor: "var(--color-border)" }} />
          </div>

          {/* Success banner */}
          {successMessage && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-3 text-sm mb-5 flex items-start gap-2">
              <CheckCircle size={15} className="mt-0.5 shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Error banner */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-5 flex items-start gap-2">
              <span className="mt-0.5 shrink-0">⚠</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
                Email address
              </label>
              <div className="relative">
                <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--color-text-muted)" }} />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setFieldErrors((p) => ({ ...p, email: undefined })); }}
                  placeholder="you@example.com"
                  className={`w-full rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none transition-colors border ${fieldErrors.email ? "border-red-400 focus:border-red-500" : "focus:border-blue-500"}`}
                  style={{
                    backgroundColor: "var(--color-bg-input)",
                    borderColor: fieldErrors.email ? undefined : "var(--color-border-input)",
                    color: "var(--color-text-primary)",
                  }}
                />
              </div>
              {fieldErrors.email && (
                <p className="text-red-600 text-xs mt-1.5">{fieldErrors.email}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium" style={{ color: "var(--color-text-secondary)" }}>
                  Password
                </label>
                <Link href="/forgot-password" className="text-xs text-blue-600 hover:text-blue-700 transition-colors">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--color-text-muted)" }} />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setFieldErrors((p) => ({ ...p, password: undefined })); }}
                  placeholder="••••••••"
                  className={`w-full rounded-xl pl-10 pr-11 py-2.5 text-sm focus:outline-none transition-colors border ${fieldErrors.password ? "border-red-400 focus:border-red-500" : "focus:border-blue-500"}`}
                  style={{
                    backgroundColor: "var(--color-bg-input)",
                    borderColor: fieldErrors.password ? undefined : "var(--color-border-input)",
                    color: "var(--color-text-primary)",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors hover:text-blue-600"
                  style={{ color: "var(--color-text-muted)" }}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="text-red-600 text-xs mt-1.5">{fieldErrors.password}</p>
              )}
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2.5 pt-0.5">
              <input
                id="remember"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer accent-blue-600"
                style={{ borderColor: "var(--color-border-input)" }}
              />
              <label htmlFor="remember" className="text-sm cursor-pointer select-none" style={{ color: "var(--color-text-muted)" }}>
                Remember me for 30 days
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || googleLoading}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed py-2.5 rounded-xl font-semibold text-sm text-white transition-all shadow-md shadow-blue-500/20 mt-1"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Signing in…
                </>
              ) : (
                <>
                  Sign In
                  <LogIn size={15} />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-sm mt-5" style={{ color: "var(--color-text-muted)" }}>
          Don&apos;t have an account?{" "}
          <Link href="/signup" className="text-blue-600 hover:text-blue-700 font-medium transition-colors">
            Create one for free
          </Link>
        </p>
      </div>
    </div>
  );
}

export default function LoginPageWrapper() {
  return (
    <Suspense fallback={
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 40%, #bfdbfe 100%)" }}
      >
        <div style={{ color: "var(--color-text-muted)" }}>Loading…</div>
      </div>
    }>
      <LoginPage />
    </Suspense>
  );
}
